import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Check, Globe } from 'lucide-react';

interface LanguageSwitcherProps {
  isMobile?: boolean;
  onLanguageChange?: () => void;
}

export function LanguageSwitcher({ isMobile = false, onLanguageChange }: LanguageSwitcherProps) {
  const { i18n, t } = useTranslation();

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    // Call the onLanguageChange callback if provided
    if (onLanguageChange) {
      onLanguageChange();
    }
  };

  const languages = [
    { code: 'en', label: 'English' },
    { code: 'el', label: 'Ελληνικά' }
  ];

  // In mobile view, render flat buttons instead of a dropdown
  if (isMobile) {
    return (
      <div className="flex flex-col space-y-1">
        {languages.map((language) => (
          <button
            key={language.code}
            type="button"
            onClick={() => changeLanguage(language.code)}
            className={`flex items-center justify-between px-2 py-1.5 rounded-md text-sm ${
              i18n.language === language.code 
                ? 'bg-primary/10 text-primary font-medium' 
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            <span className="flex items-center">
              <Globe className="h-4 w-4 mr-2" />
              {language.label}
            </span>
            {i18n.language === language.code && (
              <Check className="h-4 w-4 text-green-600" />
            )}
          </button>
        ))}
      </div>
    );
  }

  // Default dropdown view for desktop
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="px-2">
          <Globe className="h-4 w-4 mr-1" />
          <span className="uppercase">{i18n.language}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {languages.map((language) => (
          <DropdownMenuItem
            key={language.code}
            onClick={() => changeLanguage(language.code)}
            className="flex items-center justify-between"
          >
            {language.label}
            {i18n.language === language.code && (
              <Check className="h-4 w-4 ml-2 text-green-600" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
} 